/**
 * iOS push (migration 163, round 6, Sam 2026-10-27). The phone asks once, registers its APNs token with
 * register_push_token, and the server's send-push function does the rest. In the app's foreground iOS shows no banner
 * (capacitor.config presentationOptions []): the in-app toast already says it.
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '../supabaseClient';
import { APP_BUILD_VERSION } from './query/cacheVersion';

const TOKEN_KEY = 'prii.push.token';
const ASKED_KEY = 'prii.push.asked';

export function pushSupported() {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PushNotifications');
}

/** 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' | 'unsupported' */
export async function pushPermission() {
  if (!pushSupported()) return 'unsupported';
  try {
    return (await PushNotifications.checkPermissions()).receive;
  } catch {
    return 'unsupported';
  }
}

/** Whether this phone has already shown the iOS question (it can only be asked once; after that, iOS Settings). */
export function pushAsked() {
  try {
    return window.localStorage.getItem(ASKED_KEY) === '1';
  } catch {
    return true;
  }
}

/** Show the iOS question and, on yes, register for a token. Returns the permission afterwards. */
export async function requestPush() {
  if (!pushSupported()) return 'unsupported';
  try {
    window.localStorage.setItem(ASKED_KEY, '1');
  } catch {
    /* private mode */
  }
  const { receive } = await PushNotifications.requestPermissions();
  if (receive === 'granted') await PushNotifications.register();
  return receive;
}

/** Already allowed (an earlier ask, or iOS Settings): fetch the token again - it can change, and the server may have dropped it. */
export async function registerIfGranted() {
  if ((await pushPermission()) === 'granted') await PushNotifications.register();
}

/** The registration listener's half: save the token to this user. */
export async function saveToken(token) {
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* private mode */
  }
  const { error } = await supabase.rpc('register_push_token', {
    p_token: token,
    p_platform: 'ios',
    p_app_version: APP_BUILD_VERSION,
  });
  if (error) throw error;
}

/** On sign-out: this phone stops getting the signed-out account's pushes. */
export async function forgetPushToken() {
  let token = null;
  try {
    token = window.localStorage.getItem(TOKEN_KEY);
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode */
  }
  if (!token) return;
  try {
    await supabase.from('push_tokens').delete().eq('token', token);
  } catch {
    /* signed out anyway */
  }
}
